import { ColDef } from 'ag-grid-enterprise';
import { DashboardFolderT, DashboardT } from 'business/definitions';
import { formatDate } from 'helpers/formatHelper';
import { TFunction } from 'react-i18next';

/**
 * Type guard to check if the given data is of type DashboardT.
 *
 * @param data - The data to check, which can be either a DashboardT or a DashboardFolderT.
 * @returns A boolean indicating whether the data is of type DashboardT.
 */
export const isDashboardType = (
    data: DashboardT | DashboardFolderT | null | undefined,
): data is DashboardT => {
    if (!data) return false;
    return 'key' in data;
};

/**
 * Compares two dashboard or folder objects by their titles or names.
 *
 * @param valueA - The first dashboard or folder to compare.
 * @param valueB - The second dashboard or folder to compare.
 * @returns A negative number if valueA comes before valueB, a positive number if valueA comes after valueB, or 0 if they are equal.
 */
export const compareDashboardTitlesWithFolder = (
    valueA: DashboardT | DashboardFolderT,
    valueB: DashboardT | DashboardFolderT,
): number => {
    const titleA =
        typeof valueA === 'string'
            ? valueA
            : isDashboardType(valueA)
              ? valueA.title
              : valueA.name;
    const titleB =
        typeof valueB === 'string'
            ? valueB
            : isDashboardType(valueB)
              ? valueB.title
              : valueB.name;
    return titleA.localeCompare(titleB);
};

/**
 * Generates coldef for a dashboards table.
 *
 * @param isSearching - A boolean indicating whether the table is in search mode.
 *                      If `true`, an additional column for the folder is included.
 * @param t - A translation function used to localize column headers.
 * @returns An array of ColDef for the dashboards table.
 */
export const getDashboardsTableColDefs = (
    isSearching: boolean = false,
    t: TFunction,
): ColDef[] => {
    const colDefs: ColDef[] = [
        {
            field: 'title',
            headerName: t('dashboards.table.name'),
            cellRenderer: 'dashboardFolderNameCellRenderer',
            valueGetter: ({ data }: { data: DashboardT | DashboardFolderT }) =>
                data,
            comparator: compareDashboardTitlesWithFolder,
            flex: 4,
            filter: true,
            filterValueGetter: ({
                data,
            }: {
                data: DashboardT | DashboardFolderT;
            }) => (isDashboardType(data) ? data.title : data.name),
            valueFormatter: ({
                value,
            }: {
                value: DashboardT | DashboardFolderT;
            }) => (isDashboardType(value) ? value.title : value.name),
        },
        {
            field: 'modificationDate',
            headerName: t('dashboards.table.modificationDate'),
            valueGetter: ({ data }: { data: DashboardT | DashboardFolderT }) =>
                isDashboardType(data)
                    ? data.userHistory?.lastEvent?.date
                    : undefined,
            valueFormatter: ({ value }: { value: string | undefined }) =>
                value ? formatDate(new Date(value)) : '-',
            flex: 2,
            filter: 'agDateColumnFilter',
            filterValueGetter: ({
                data,
            }: {
                data: DashboardT | DashboardFolderT;
            }) =>
                isDashboardType(data) && data.userHistory?.lastEvent?.date
                    ? new Date(data.userHistory.lastEvent.date).setHours(
                          0,
                          0,
                          0,
                          0,
                      )
                    : null,
        },
        {
            field: 'modifiedBy',
            headerName: t('dashboards.table.modifiedBy'),
            valueGetter: ({ data }: { data: DashboardT | DashboardFolderT }) =>
                isDashboardType(data)
                    ? data.userHistory?.lastEvent?.userDisplayName
                    : undefined,
            flex: 2,
            filter: true,
        },
        {
            field: 'accessCount',
            headerName: t('dashboards.table.accessCount'),
            valueGetter: ({ data }: { data: DashboardT | DashboardFolderT }) =>
                isDashboardType(data) ? data : undefined,
            cellRenderer: 'accessCountCellRenderer',
            comparator: (
                valueA: DashboardT | DashboardFolderT,
                valueB: DashboardT | DashboardFolderT,
            ) => {
                if (!isDashboardType(valueA) || !isDashboardType(valueB))
                    return 0;
                const sumA =
                    (valueA.readUserAccessCount || 0) +
                    (valueA.writeUserAccessCount || 0);
                const sumB =
                    (valueB.readUserAccessCount || 0) +
                    (valueB.writeUserAccessCount || 0);
                return sumA - sumB;
            },
            flex: 2,
            filter: 'agNumberColumnFilter',
            filterValueGetter: ({
                data,
            }: {
                data: DashboardT | DashboardFolderT;
            }) =>
                isDashboardType(data)
                    ? (data.readUserAccessCount || 0) +
                      (data.writeUserAccessCount || 0)
                    : null,
        },
        ...(isSearching
            ? [
                  {
                      field: 'folder',
                      headerName: t('dashboards.table.folder'),
                      valueGetter: ({
                          data,
                      }: {
                          data: DashboardT | DashboardFolderT;
                      }) =>
                          isDashboardType(data) ? data.folder?.name : undefined,
                      flex: 2,
                      filter: true,
                  },
              ]
            : []),
        {
            field: 'action',
            headerName: t('dashboards.table.action'),
            valueGetter: ({ data }: { data: DashboardT | DashboardFolderT }) =>
                data,
            cellRenderer: 'actionCellRenderer',
            sortable: false,
            cellStyle: { cursor: 'unset' },
            flex: 1,
            filter: false,
        },
    ];

    return colDefs;
};
